import { toast } from 'react-toastify';
import { all, call, debounce, fork, put, select, takeLatest, takeLeading } from 'redux-saga/effects';

import { checkUsername, deleteUser, fetchAllBasicUsers, fetchAllUsers, fetchAvailableFeaturePermissions, resetPassword, upsertUser } from '../../../../services/users';
import { updateClientUsers } from '../../clients/store/actions';
import { checkUsernameAvailableFailed, checkUsernameAvailableSuccessful, deleteUserFailed, deleteUserStarted, deleteUserSuccessful, fetchAllBasicUsersFailed, fetchAllBasicUsersSuccessful, fetchAllUsersFailed, fetchAllUsersSuccessful, fetchAvailableFeaturePermissionsFailed, fetchAvailableFeaturePermissionsSuccessful, resetUserPassword, upsertUserFailed, upsertUserStarted, upsertUserSuccessful } from './actions';
import { getNewUser } from './selectors';
import { BasicUserInfo, ClientUser, FeaturePermissionDB, NewUser, UsersActionTypes } from './types';
import { AccessGroupTableName } from '../../access-groups/store/types';
import { getAccessGroupColumnSort, getAccessGroupFilters } from '../../access-groups/store/selectors';
import { TableFilters, ColumnSort } from '../../../shared/table/types';

export function* attemptUpsertUser({ payload }: ReturnType<typeof upsertUserStarted>) {
    try {
        const newUser: NewUser = yield select(getNewUser);
        const { userId, clientId } = newUser;
        const allUsers: ClientUser[] = yield call(upsertUser, { newUser });
        if (payload && clientId) {
            yield put(updateClientUsers(clientId, allUsers));
        } else {
            yield put(upsertUserSuccessful(allUsers));
        }
        const successToastMessage = userId ? 'Successfully updated user details' : 'Successfully added a new user';
        toast(successToastMessage);
    } catch (e) {
        yield put(upsertUserFailed((e as Error).message));
        toast.error('Unable to save the user. Please try again.');
    }
}

function* upsertUserWatcher() {
    yield takeLeading(UsersActionTypes.UPSERT_USER_STARTED, attemptUpsertUser);
}

export function* attemptCheckUsername() {
    try {
        const { username, userId } = yield select(getNewUser);
        const usernameUnavailable: boolean = yield call(checkUsername, { username, userId });
        yield put(checkUsernameAvailableSuccessful(usernameUnavailable));
    } catch (e) {
        yield put(checkUsernameAvailableFailed((e as Error).message));
    }
}

function* checkUsernameWatcher() {
    yield debounce(1000, UsersActionTypes.CHECK_USERNAME_AVAILABLE_STARTED, attemptCheckUsername);
}

export function* attemptFetchAllUsers() {
    try {
        const filters: TableFilters = yield select(getAccessGroupFilters(AccessGroupTableName.USER));
        const columnSort: ColumnSort = yield select(getAccessGroupColumnSort(AccessGroupTableName.USER));
        const users: ClientUser[] = yield call(fetchAllUsers, { filters, columnSort });
        yield put(fetchAllUsersSuccessful(users));
    } catch (e) {
        toast.error('Unable to fetch the latest list of users. Please try again.');
        yield put(fetchAllUsersFailed((e as Error).message));
    }
}

function* fetchAllUsersWatcher() {
    yield takeLatest(UsersActionTypes.FETCH_ALL_USERS_STARTED, attemptFetchAllUsers);
}

export function* attemptFetchAllBasicUsers() {
    try {
        const users: BasicUserInfo[] = yield call(fetchAllBasicUsers);
        yield put(fetchAllBasicUsersSuccessful(users));
    } catch (e) {
        toast.error('Unable to fetch the latest list of users. Please try again.');
        yield put(fetchAllBasicUsersFailed((e as Error).message));
    }
}

function* fetchAllBasicUsersWatcher() {
    yield takeLeading(UsersActionTypes.FETCH_ALL_BASIC_USERS_STARTED, attemptFetchAllBasicUsers);
}

export function* attemptFetchAvailableFeaturePermissions() {
    try {
        const permissions: FeaturePermissionDB[] = yield call(fetchAvailableFeaturePermissions);
        yield put(fetchAvailableFeaturePermissionsSuccessful(permissions));
    } catch (e) {
        toast.error('Unable to fetch feature permissions.');
        yield put(fetchAvailableFeaturePermissionsFailed((e as Error).message));
    }
}

function* fetchAvailableFeaturePermissionsWatcher() {
    yield takeLeading(UsersActionTypes.FETCH_AVAILABLE_FEATURE_PERMISSIONS_STARTED, attemptFetchAvailableFeaturePermissions);
}

export function* attemptDeleteUser({ payload }: ReturnType<typeof deleteUserStarted>) {
    try {
        const { userId, clientId, username } = payload;
        const allUsers: ClientUser[] = yield call(deleteUser, { userId, clientId });
        if (clientId) {
            yield put(updateClientUsers(clientId, allUsers));
        } else {
            yield put(deleteUserSuccessful(allUsers));
        }
        toast(`${username} successfully deleted.`);
    } catch (e) {
        toast.error('Unable to delete user. Please try again.');
        yield put(deleteUserFailed((e as Error).message));
    }
}

function* deleteUserWatcher() {
    yield takeLeading(UsersActionTypes.DELETE_USER_STARTED, attemptDeleteUser);
}

export function* attemptResetUserPassword({ payload }: ReturnType<typeof resetUserPassword>) {
    try {
        const { username } = payload;
        yield call(resetPassword, { username });
        toast(`Password reset link sent to ${username}.`);
    } catch (e) {
        toast.error('Unable to send password reset link. Please try again.');
    }
}

function* resetUserPasswordWatcher() {
    yield takeLeading(UsersActionTypes.RESET_USER_PASSWORD, attemptResetUserPassword);
}

export function* userSaga() {
    yield all([
        fork(upsertUserWatcher),
        fork(checkUsernameWatcher),
        fork(fetchAllUsersWatcher),
        fork(fetchAllBasicUsersWatcher),
        fork(fetchAvailableFeaturePermissionsWatcher),
        fork(deleteUserWatcher),
        fork(resetUserPasswordWatcher)
    ]);
}
